import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Row,
  Spin,
  Modal,
  Input,
  message,
  Space,
  Tabs,
} from "antd";
import { MailOutlined, WhatsAppOutlined } from "@ant-design/icons";
import { getCookie } from "../../../helpers/localStorage";
import axiosInstance from "../../../config/axios.config";
import useIsMobile from "../../../hooks/useIsMobile";
import ScaledHtmlPreview from "../../ui/ScaledHtmlPreview";
import OpSummaryEmptyState, {
  isEmptySummaryHtml,
} from "../../Receptionist/Outpatients/OpSummaryEmptyState";
import "./summaryOpMobile.css";

const SummaryOP = ({ emrId, serviceType }) => {
  const isMobile = useIsMobile();
  const [data, setData] = useState("<p>Loading...</p>");
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [activeTab, setActiveTab] = useState("email");

  const queryParams = new URLSearchParams(window.location.search);
  const admissionId = queryParams.get("aid");
  const patientId = queryParams.get("pid");

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const token = getCookie("accessToken");
        const res = await axiosInstance.get(
          `/outpatient/services/${serviceType}/download/${emrId}/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setData(res.data || "<p>No summary available</p>");
      } catch (err) {
        console.error("Fetch Summary Error:", err);
        setData("<p>Failed to load summary.</p>");
        message.error(
          err.response?.status === 401
            ? "Session expired. Please login again."
            : "Failed to fetch summary."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [emrId, serviceType]);

  const openShareModal = async () => {
    if (!admissionId) return message.error("Admission ID not found in URL.");
    try {
      const token = getCookie("accessToken");
      const res = await axiosInstance.get(
        `/outpatient/emr/${patientId}/admission/${admissionId}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmail(res.data?.data?.patient_data?.email || "");
      setMobile(res.data?.data?.patient_data?.mobile || "");
      setShareModalVisible(true);
    } catch (err) {
      console.error("Fetch Patient Email Error:", err);
      message.error("Failed to fetch patient email.");
    }
  };

  const handleShareEmail = async () => {
    if (!email) return message.warning("Please enter an email address.");
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      return message.error("Please enter correct email address");
    }
    setShareLoading(true);
    try {
      const token = getCookie("accessToken");
      const response = await axiosInstance.get(
        `/outpatient/services/${serviceType}/download/${emrId}/?share=email&email=${encodeURIComponent(
          email
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(response?.data?.message || "Shared successfully!");
      setShareModalVisible(false);
    } catch (err) {
      console.error("Share Error:", err);
      message.error("Failed to share summary.");
    } finally {
      setShareLoading(false);
    }
  };

  const handleShareWhatsApp = async () => {
    if (!mobile) return message.warning("Please enter a mobile number.");
    const mobilePattern = /^[+]?[\d\s\-\(\)]+$/;
    if (!mobilePattern.test(mobile)) {
      return message.error("Please enter a valid mobile number");
    }
    setShareLoading(true);
    try {
      const token = getCookie("accessToken");
      const response = await axiosInstance.get(
        `/outpatient/services/${serviceType}/download/${emrId}/?share=whatsapp&recipient=${encodeURIComponent(
          mobile
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(response?.data?.message || "Shared successfully!");
      setShareModalVisible(false);
    } catch (err) {
      console.error("Share Error:", err);
      message.error("Failed to share summary.");
    } finally {
      setShareLoading(false);
    }
  };

  const tabItems = [
    {
      key: "email",
      label: (
        <span>
          <MailOutlined style={{ marginRight: 8 }} />
          Email
        </span>
      ),
      children: (
        <Space direction="vertical" style={{ width: "100%", marginTop: 16 }}>
          <Input
            placeholder="Enter email address"
            prefix={<MailOutlined />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="large"
          />
          <Button
            type="primary"
            icon={<MailOutlined />}
            onClick={handleShareEmail}
            loading={shareLoading}
            size="large"
          >
            Share via Email
          </Button>
        </Space>
      ),
    },
    {
      key: "whatsapp",
      label: (
        <span>
          <WhatsAppOutlined style={{ marginRight: 8 }} />
          WhatsApp
        </span>
      ),
      children: (
        <Space direction="vertical" style={{ width: "100%", marginTop: 16 }}>
          <Input
            placeholder="Enter mobile number"
            prefix={<WhatsAppOutlined />}
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            size="large"
          />
          <Button
            type="primary"
            icon={<WhatsAppOutlined />}
            onClick={handleShareWhatsApp}
            loading={shareLoading}
            size="large"
            // style={{ backgroundColor: "#25D366", borderColor: "#25D366" }}
          >
            Share via WhatsApp
          </Button>
        </Space>
      ),
    },
  ];

  const showActions = !loading && !isEmptySummaryHtml(data);

  return (
    <>
      <Row gutter={[16, 16]}>
        {showActions && (
        <Col span={24} style={{ textAlign: "right", marginBottom: 16 }}>
          <Space size={8}>
            <Button
              type="primary"
              loading={btnLoading}
              onClick={async () => {
                try {
                  const token = getCookie("accessToken");
                  const res = await axiosInstance.get(
                    `/outpatient/services/${serviceType}/download/${emrId}/`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );

                  if (res?.data) {
                    const iframe = document.createElement("iframe");
                    document.body.appendChild(iframe);

                    const iframeDoc =
                      iframe.contentDocument || iframe.contentWindow.document;
                    iframeDoc.open();
                    iframeDoc.write(res.data);
                    iframeDoc.close();

                    setTimeout(() => {
                      iframe.contentWindow.print();
                      document.body.removeChild(iframe);
                    }, 300);
                  }
                } catch (err) {
                  console.error("Print failed", err);
                  message.error("Failed to print summary");
                }
              }}
            >
              Print Summary
            </Button>
            <Button type="default" onClick={openShareModal}>
              Share
            </Button>
          </Space>
        </Col>
        )}

        <Col span={24} style={{ width: "100%" }}>
          <div className="op-summary-view">
            {loading ? (
              <Spin
                size="large"
                style={{ margin: "50px auto", display: "block" }}
              />
            ) : isEmptySummaryHtml(data) ? (
              <OpSummaryEmptyState />
            ) : (
              <ScaledHtmlPreview
                html={data}
                designWidth={800}
                className="ip-form-html-preview summary-container"
              />
            )}
          </div>
        </Col>
      </Row>

      <Modal
        open={shareModalVisible}
        onCancel={() => {
          setShareModalVisible(false);
          setActiveTab("email");
        }}
        footer={null}
        destroyOnClose={false}
        width={isMobile ? "min(92vw, 400px)" : 500}
        centered={isMobile}
      >
        <Input
          placeholder="Enter email address"
          prefix={<MailOutlined />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ marginBottom: 16, marginTop: 20 }}
        />
        <Space size={8}>
          <Button
            type="primary"
            icon={<MailOutlined />}
            onClick={handleShareEmail}
            loading={shareLoading}
          >
            Share via Email
          </Button>
          {/* <Button
            type="default"
            icon={<WhatsAppOutlined />}
            onClick={() => message.info("WhatsApp share coming soon")}
          >
            Share via WhatsApp
          </Button> */}
        </Space>
      </Modal>
    </>
  );
};

export default SummaryOP;
